import React, { useState, useEffect } from "react";
import styled from "styled-components";

const Container = styled.div`
  max-width: 600px;
  margin: 30px auto;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
`;

const Button = styled.button`
  padding: 8px 12px;
  border-radius: 8px;
  border: none;
  background: ${(props) => props.color || "#234ffc"};
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s ease;

  &:hover {
    background: ${(props) => props.hover || "#123eeb"};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  margin-left: 8px;
`;

export default function WynikiForm({ rocznikId }) {
  const [formData, setFormData] = useState({
    rywal: "",
    punktyN: "",
    punktyR: "",
    logoRywal: "",
    kolorRywal: "#234ffc",
  });
  const [loading, setLoading] = useState(false);
  const [wyniki, setWyniki] = useState([]);
  const [matches, setMatches] = useState([]);
  const [selectedMatchId, setSelectedMatchId] = useState(null);

  // Pobierz wyniki
  const fetchWyniki = async () => {
    try {
      const res = await fetch(`https://api.pth-hokej.pl/api/roczniki/${rocznikId}/wyniki`);
      const data = await res.json();
      setWyniki(data);
    } catch (err) {
      console.error(err);
    }
  };

  // Pobierz zakończone mecze
  // Pobierz zakończone mecze
  const fetchMatches = async () => {
    try {
      const res = await fetch(`https://api.pth-hokej.pl/api/matches/rocznik/${rocznikId}`);
      const data = await res.json();

      // Filtruj mecze zakończone (data + godzina co najmniej 2h wcześniej)
      const finished = data.filter(m => {
        const matchDateTime = new Date(`${m.date}T${m.time}`);
        const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);
        return matchDateTime <= twoHoursAgo;
      });

      setMatches(finished);
    } catch (err) {
      console.error(err);
    }
  };


  useEffect(() => {
    if (rocznikId) {
      fetchWyniki();
      fetchMatches();
    }
  }, [rocznikId]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Dodaj wynik
      await fetch(`https://api.pth-hokej.pl/api/roczniki/${rocznikId}/wyniki`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      // Jeśli był wybrany mecz, usuń go z bazy
      if (selectedMatchId) {
        await fetch(`https://api.pth-hokej.pl/api/matches/${selectedMatchId}`, { method: "DELETE" });
        setSelectedMatchId(null);
        fetchMatches();
      }

      setFormData({
        rywal: "",
        punktyN: "",
        punktyR: "",
        logoRywal: "",
        kolorRywal: "#234ffc",
      });
      fetchWyniki();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddResult = (match) => {
    setSelectedMatchId(match._id);
    setFormData({
      rywal: match.opponentName || "",
      punktyN: match.punktyN || "",
      punktyR: match.punktyR || "",
      logoRywal: match.opponentLogo || "",
      kolorRywal: "#234ffc", // wymuszenie wyboru koloru przez użytkownika
    });
  };

  return (
    <Container>
      <h3>Zakończone mecze rocznika</h3>
      {matches.length === 0 && <p>Brak zakończonych meczów.</p>}
      <ul>
        {matches.map((m) => (
          <li key={m._id} style={{ marginBottom: "8px" }}>
            <strong>{m.opponentName}</strong> ({m.date} {m.time})
            <Button
              type="button"
              color="#5bc0de"
              hover="#31b0d5"
              onClick={() => handleAddResult(m)}
            >
              Dodaj wynik
            </Button>
            <Button
              type="button"
              color="#d9534f"
              hover="#c9302c"
              onClick={async () => {
                if (!window.confirm("Na pewno chcesz usunąć ten mecz?")) return;
                await fetch(`https://api.pth-hokej.pl/api/matches/${m._id}`, { method: "DELETE" });
                fetchMatches();
              }}
            >
              Usuń mecz
            </Button>
          </li>
        ))}
      </ul>

      <h3>Dodaj wynik dla rocznika</h3>
      <Form onSubmit={handleSubmit}>
        <Input
          name="rywal"
          placeholder="Nazwa rywala"
          value={formData.rywal}
          onChange={handleChange}
        />
        <Input
          name="punktyN"
          type="number"
          placeholder="Punkty naszej drużyny"
          value={formData.punktyN}
          onChange={handleChange}
        />
        <Input
          name="punktyR"
          type="number"
          placeholder="Punkty rywala"
          value={formData.punktyR}
          onChange={handleChange}
        />
        <Input
          name="logoRywal"
          placeholder="Link do logo rywala"
          value={formData.logoRywal}
          onChange={handleChange}
        />
        <label>
          Kolor rywala:
          <Input
            type="color"
            name="kolorRywal"
            value={formData.kolorRywal}
            onChange={handleChange}
          />
        </label>
        <Button type="submit" disabled={loading}>
          Dodaj wynik
        </Button>
      </Form>

      <h4>Wyniki:</h4>
      <ul>
        {wyniki.length > 0 && wyniki.map((w, idx) => (
          <li key={idx}>
            {w.rywal}: {w.punktyN} - {w.punktyR}
          </li>
        ))}
      </ul>
    </Container>
  );
}
